import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useCallback, useState, Fragment, FC, SyntheticEvent } from 'react'

import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'

const Portal = dynamic(() => import('~/components/Portal'), { ssr: false })

const mediaQuery = [{ screen: '(min-width: 300px)', width: '36px' }]
const awsImageMediaQuery = [{ screen: '(min-width: 300px)', width: '528px' }]

function checkSvgUrl(url: string): boolean {
  return /\.svg$/.test(url)
}

// React bubbles portal events to the image's parents: keep them from answer options and drag cards
const stopPropagation = (e: SyntheticEvent): void => e.stopPropagation()

const BootcampAwsImageModal: FC<{ props: any; onClose?: () => void }> = ({ props, onClose = () => {} }) => {
  const { t } = useTranslation('bootcamp')
  return (
    <>
      <div
        className="relative bg-white h-full w-full md:h-5/6 md:w-5/6 lg:h-3/4 lg:w-3/4
        place-self-center rounded z-10 p-3 flex flex-col drop-shadow-lg"
      >
        <TransformWrapper>
          {({ zoomIn, zoomOut }) => (
            <Fragment>
              <div className="h-12 flex flex-row items-start">
                <div className="flex-1 flex flex-col">
                  <div className="flex flex-row gap-6">
                    <ResponsiveImage
                      alt={t('imageAlts.zoomInButton')}
                      className="cursor-pointer"
                      height={36}
                      mediaQuery={mediaQuery}
                      onClick={() => zoomIn()}
                      srcKey="Math Vault/icons/ZoomInIcon.svg"
                      width={36}
                    />
                    <ResponsiveImage
                      alt={t('imageAlts.zoomOutButton')}
                      className="cursor-pointer"
                      height={36}
                      mediaQuery={mediaQuery}
                      onClick={() => zoomOut()}
                      srcKey="Math Vault/icons/ZoomOutIcon.svg"
                      width={36}
                    />
                  </div>
                </div>
                <ResponsiveImage
                  alt={t('imageAlts.closeButton')}
                  className="cursor-pointer"
                  height={24}
                  mediaQuery={mediaQuery}
                  onClick={onClose}
                  srcKey="Math Vault/icons/close-btn.svg"
                  width={24}
                />
              </div>
              <TransformComponent
                contentClass="flex-1 !w-full cursor-pointer flex justify-center content-center"
                wrapperClass="flex-1 flex flex-col !w-full rounded"
              >
                <div className="h-full">
                  {checkSvgUrl(props.node.srcKey) ? (
                    <SVGImage
                      alt={props.node.alt}
                      className="h-full block"
                      height={props.node.height ?? 185}
                      imageClassName="w-full h-full object-contain"
                      loading="eager"
                      srcKey={props.node.srcKey}
                      width={props.node.width}
                    />
                  ) : (
                    <ResponsiveImage
                      alt={props.node.alt}
                      height={props.node.height}
                      imageClassName="w-full object-contain"
                      loading="eager"
                      srcKey={props.node.srcKey}
                      width={props.node.width}
                    />
                  )}
                </div>
              </TransformComponent>
            </Fragment>
          )}
        </TransformWrapper>
      </div>
      <div className="bg-white opacity-30 absolute top-0 w-full h-full" onClick={onClose} />
    </>
  )
}

const BootcampAwsImage: FC<any> = (props) => {
  const { zoomable } = props.node
  const [modalOpened, setModalOpened] = useState(false)

  // The image components type onClick as () => void but pass the click event through
  const onImageClick = useCallback(
    (e?: SyntheticEvent) => {
      if (!zoomable) return
      // Answer elements (options, tiles, drag cards) are marked data-no-image-zoom: there a tap answers,
      // and an image-only option would otherwise be unselectable. Only the question's images zoom
      if (e?.currentTarget instanceof Element && e.currentTarget.closest('[data-no-image-zoom]')) return
      // Opening the zoom must not also select whatever else the image sits in
      e?.stopPropagation()
      setModalOpened(true)
    },
    [zoomable],
  )

  return (
    <>
      {checkSvgUrl(props.node.srcKey) ? (
        <SVGImage
          alt={props.node.alt}
          className="h-full block"
          height={props.node.height ?? 185}
          imageClassName="activity-question-image mx-auto max-w-full h-full object-contain"
          loading="eager"
          onClick={onImageClick}
          srcKey={props.node.srcKey}
          width={props.node.width}
        />
      ) : (
        <ResponsiveImage
          alt={props.node.alt}
          className="h-full w-full"
          fit="contain"
          height={props.node.height ?? 192}
          imageClassName="activity-question-image w-full max-h-full object-contain"
          imageWidths="400, 528, 600"
          loading="eager"
          mediaQuery={awsImageMediaQuery}
          onClick={onImageClick}
          srcKey={props.node.srcKey}
          types={['webp', 'png']}
          width={props.node.width ?? 528}
        />
      )}

      {modalOpened && (
        // z-top: above the activity window, the keyboard and the Oracle
        <Portal targetDomId="__next" wrapperClassNames="fixed z-top inset-0 grid p-2">
          <div
            className="contents"
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
            onPointerDown={stopPropagation}
            onTouchStart={stopPropagation}
          >
            <BootcampAwsImageModal onClose={() => setModalOpened(false)} props={props} />
          </div>
        </Portal>
      )}
    </>
  )
}

export default BootcampAwsImage
